import { Task } from '@mui/icons-material'
import { darken } from '@mui/material'
import axios, {
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig
} from 'axios'
import dayjs from 'dayjs'
import { DocType, EventDoc, EventStatus, TaskType } from 'types/api/changes'
import { LocalStorageKey } from 'types/services/localStorage'
import { getAccessToken } from 'utils/auth'
import { icloudPrefix } from 'utils/constants'
import { isTestEnv } from 'utils/env'
import { isTrulyEmpty } from 'utils/object'
import { uuid } from 'utils/uuid'

import { LOGIN_URL } from './api/auth'
import LocalStorage from './LocalStorage'
import Logger from './Logger'

export const fetchCalendars = '/calendars'
export const fetchCalendarItems = '/calendars/items'
export const addCalendarItem = '/calendarobject/create'
export const updateCalendarItem = '/calendarobject/update'
export const deleteCalendarItem = '/calendarobject/delete'

export function API_URL() {
  return isTestEnv() && LocalStorage.get(LocalStorageKey.APP_HOST)
    ? LocalStorage.get(LocalStorageKey.APP_HOST)
    : (process.env.REACT_APP_API_URL ?? 'https://stage001.twentyfour.me')
}

class IcloudApi {
  instance = axios.create({
    baseURL: `${API_URL()}/api/v1/icloud`
  })

  constructor() {
    // Updated interceptor for Axios 1.x
    this.instance.interceptors.request.use(
      (req: InternalAxiosRequestConfig) => {
        if (getAccessToken()) {
          const token = getAccessToken() as string
          const skipAuth = req.url?.includes(LOGIN_URL)

          // Use proper header setting method
          if (!skipAuth && token) {
            req.headers.set('Authorization', token)
          }
        }
        return req
      }
    )
  }

  private post(
    url: string,
    body: Record<string, string | number | object | undefined | null>,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    const postBody = {
      ...body
    }

    return this.instance.post(url, postBody, config).then((e) => {
      return e
    })
  }

  public async getCalendars(data: any): Promise<any[]> {
    return (await this.post(fetchCalendars, data)).data
  }

  public async addCalendarItemToIcloud(
    addCalendarData: AddCalendarData
  ): Promise<any> {
    return (
      await this.post(addCalendarItem, { ...addCalendarData, _id: uuid() })
    ).data
  }

  public async updateCalendarItemToIcloud(
    addCalendarData: AddCalendarData
  ): Promise<any> {
    return (await this.post(updateCalendarItem, { ...addCalendarData })).data
  }

  public async deleteCalendarItemFromIcloud(
    deleteEventData: DeleteEventData
  ): Promise<any> {
    return await this.post(deleteCalendarItem, { ...deleteEventData })
  }

  public getCalendarItems(
    email: string,
    calendar: any,
    start: string,
    end: string,
    searchQuery?: string
  ): Promise<EventDoc[]> {
    let timeRangeStart = start
    let timeRangeEnd = end

    if (!start || isTrulyEmpty(start)) {
      timeRangeStart = dayjs().startOf('week').toISOString()
    }

    if (!end || isTrulyEmpty(end)) {
      timeRangeEnd = dayjs().endOf('week').add(1, 'month').toISOString()
    }

    const requestBody: any = {
      email: email,
      calendar: calendar
    }

    // When search query is provided, construct CalDAV filter and skip timeRange to search entire user items
    if (searchQuery && searchQuery.length > 0) {
      //TODO: implement search for iCloud CalDav
      Logger.red('iCloud search is not implemented yet.')

      // requestBody.filters = {
      //   'comp-filter': {
      //     _attributes: { name: 'VCALENDAR' },
      //     'comp-filter': {
      //       _attributes: { name: 'VEVENT' },
      //       'prop-filter': [
      //         {
      //           _attributes: { name: 'SUMMARY' },
      //           'text-match': {
      //             _attributes: { collation: 'i;ascii-casemap' },
      //             _text: searchQuery
      //           }
      //         },
      //         {
      //           _attributes: { name: 'DESCRIPTION' },
      //           'text-match': {
      //             _attributes: { collation: 'i;ascii-casemap' },
      //             _text: searchQuery
      //           }
      //         },
      //         {
      //           _attributes: { name: 'LOCATION' },
      //           'text-match': {
      //             _attributes: { collation: 'i;ascii-casemap' },
      //             _text: searchQuery
      //           }
      //         }
      //       ]
      //     }
      //   }
      // }
    } else {
      requestBody.timeRange = {
        start: start,
        end: end
      }
    }

    return this.post(fetchCalendarItems, requestBody).then((response) => {
      return response.data.map((calDavEventData: IcloudApiEvent) => {
        return convertIcloudEventToEventDoc(calDavEventData, email, calendar)
      })
    })
  }
}

export default new IcloudApi()

export interface AddCalendarData {
  calendar?: string
  email: string
  filename?: string
  iCalEventString?: string
  event: EventDoc
  url?: string
  etag?: string
}

export function convertIcloudEventToEventDoc(
  calDavEventData: IcloudApiEvent,
  email: string,
  calendar: any
) {
  const event: EventDoc = {
    ...calDavEventData,
    attendees: calDavEventData.Attendees || [],
    etag: calDavEventData.etag,
    ConferenceData:
      typeof calDavEventData?.ConferenceData !== 'string'
        ? calDavEventData.ConferenceData
        : undefined,
    _id: calDavEventData._id,
    local_id: calDavEventData._id,
    Rank: calDavEventData.CreatedDate,
    GroupID: `${icloudPrefix}${email}`,
    Type: DocType.EVENT,
    TaskType: TaskType.personalEvent,
    LastUpdate: extractModifiedDateFromCalDavString(
      calDavEventData.icalString,
      calDavEventData.CreatedDate
    ),
    Start: calDavEventData.Start,
    End: calDavEventData.End,
    Status: EventStatus.ACTIVE,
    OwnerID: LocalStorage.get(LocalStorageKey.USER_ID) ?? '',
    UserID: LocalStorage.get(LocalStorageKey.USER_ID) ?? '',
    PersonalOwnerId: email,
    ThirdPartyID: calendar.url,
    ThirdPartyLastUpdate: extractModifiedDateFromCalDavString(
      calDavEventData.icalString,
      calDavEventData.CreatedDate
    ),
    Color: darken(
      calendar.calendarColor.length > 0 ? calendar.calendarColor : '#3293eb',
      0.15
    )
  }
  return event
}

function extractModifiedDateFromCalDavString(
  icalString: string,
  fallback: string
) {
  // extract LAST-MODIFIED:20251008T134000Z from string, and convert to dayjs unix seconds
  const regex = /LAST-MODIFIED:(\d{8}T\d{6}Z)/i
  const match = icalString.match(regex)
  if (match && match[1]) {
    const dateStr = match[1]
    // Parse the date: format is YYYYMMDDTHHmmssZ
    // dayjs expects ISO format, so we need to convert: 20251008T134000Z -> 2025-10-08T13:40:00Z
    const formattedDate = `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}-${dateStr.slice(6, 8)}T${dateStr.slice(9, 11)}:${dateStr.slice(11, 13)}:${dateStr.slice(13, 15)}Z`
    const unixTimestamp = dayjs(formattedDate).unix().toString()

    return unixTimestamp
  } else {
    return fallback
  }
}

export interface IcloudApiEvent extends EventDoc {
  icalString: string
  icalObject2: any
  CreatedDate: string
}

export interface DeleteEventData {
  email: string
  url?: string
}
